import React from 'react';

import { ActionIcon, Box } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';

import { formatUtcDate } from '@/utils/formatDate';
import { routes } from '@/routes';
import ClinicalNoteActivityFeedIcon from '@/assets/icons/ClinicalNoteActivityFeedIcon';
import { MailIcon } from '@/assets/icons/MailIcon';
import { OutboundCallIcon } from '@/assets/icons/OutboundCallIcon';
import PatientActivityFeedIcon from '@/assets/icons/PatientActivityFeedIcon';
import RedAlertActivityFeedIcon from '@/assets/icons/RedAlertActivityFeedIcon';
import RegalOutboundCallIcon from '@/assets/icons/RegalInboundCallIcon';
import RegalInboundCallIcon from '@/assets/icons/RegalOutboundCallIcon';
import ReportActivityFeedIcon from '@/assets/icons/ReportActivityFeedIcon';
import YellowAlertActivityFeedIcon from '@/assets/icons/YellowAlertActivityFeedIcon';

import { ActivityFeedItem, ActivityFeedType } from '@/types/activityFeed';

import styles from '../ActivityFeed.module.scss';
import { formatCallDuration, formatTimeDate } from '../utils';

// Activity type to icon mapping
const ACTIVITY_ICONS = {
  [ActivityFeedType.TRANSMISSION]: {
    RED: <RedAlertActivityFeedIcon />,
    YELLOW: <YellowAlertActivityFeedIcon />,
    default: <ReportActivityFeedIcon />
  },
  [ActivityFeedType.CLINICAL_NOTE]: <ClinicalNoteActivityFeedIcon />,
  [ActivityFeedType.PATIENT_STATUS]: <PatientActivityFeedIcon />,
  [ActivityFeedType.REGAL_CALL]: {
    OUTBOUND: <RegalOutboundCallIcon />,
    INBOUND: <RegalInboundCallIcon />
  },
  [ActivityFeedType.POSTGRID]: <MailIcon />,
  [ActivityFeedType.RETELL_CALLS]: <OutboundCallIcon />
};

// Activity title generators
const ACTIVITY_TITLES = {
  [ActivityFeedType.TRANSMISSION]: (item: ActivityFeedItem) => {
    if (item.data.color === 'RED') {
      return `Red Alert (${item.data.transmissionType})`;
    } else if (item.data.color === 'YELLOW') {
      return `Yellow Alert (${item.data.transmissionType})`;
    }
    return `Routine Report (${item.data.transmissionType})`;
  },
  [ActivityFeedType.CLINICAL_NOTE]: () => 'Clinical Note',
  [ActivityFeedType.PATIENT_STATUS]: () => 'Patient Status Change',
  [ActivityFeedType.REGAL_CALL]: (item: ActivityFeedItem) => {
    const duration = item.data.duration ? formatCallDuration(item.data.duration) : '00:00';
    return `${item.data.direction === 'INBOUND' ? 'Inbound' : 'Outbound'} Call (${duration})`;
  },
  [ActivityFeedType.POSTGRID]: () => 'Mail Sent',
  [ActivityFeedType.RETELL_CALLS]: () => 'Outbound Call'
};

interface ActivityItemProps {
  item: ActivityFeedItem;
}

export const ActivityItem: React.FC<ActivityItemProps> = ({ item }) => {
  const timeDateResult = formatTimeDate(item?.data?.transmissionDate ?? item?.eventDate);
  const { date, time } =
    typeof timeDateResult === 'string' ? { date: '', time: '' } : timeDateResult;

  const getTitle = () => {
    const titleFn = ACTIVITY_TITLES[item.eventType];
    const totalDuration =
      item.eventType === ActivityFeedType.RETELL_CALLS
        ? formatDuration(item?.data?.total_duration_seconds)
        : undefined;
    if (item.eventType === ActivityFeedType.RETELL_CALLS) {
      return `Outbound Call (${totalDuration})`;
    }
    return titleFn ? titleFn(item) : 'Activity';
  };

  const formatDuration = (seconds: number | undefined) => {
    if (!seconds && seconds !== 0) return '00:00:00';

    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    const pad = (num: number) => String(num).padStart(2, '0');

    return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
  };
  const getIcon = () => {
    const iconSet = ACTIVITY_ICONS[item.eventType] as any;
    if (typeof iconSet === 'object' && item.data.color) {
      return iconSet[item.data.color] || iconSet.default;
    }
    if (typeof iconSet === 'object' && item.data.direction) {
      return iconSet[item.data.direction] || iconSet.default;
    }
    return iconSet || <ReportActivityFeedIcon />;
  };

  const handleOpenTxReport = () => {
    const reportId = item?.data?.id;
    if (reportId) {
      window.open(`${routes.physician.transmissionReportDetail(reportId.toString())}`, '_blank');
    } else {
      console.error('Missing required query parameters: reportId or color');
    }
  };

  const formatReason = (reason: string | undefined) =>
    reason
      ?.split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ') || '-';

  const formatReasonNoted = (reason: string | undefined) =>{
    switch(reason){
      case 'call_transfer':
        return 'Call transferred to device support team for further assistance.';
      case 'voicemail_reached':
        return 'Recipient did not answer call, message left to return call at their earliest convenience.';
      case 'user_hangup':
        return 'Call disconnected by recipient.';
      case 'agent_hangup':
        return 'Unable to leave a voicemail or call disconnected/lost.';
      default:
        return '-';
    }
  }
    

  const regalCallFields: {
    label: string;
    value: string | undefined;
  }[] = [
    { label: 'Disposition', value: item.data.disposition ?? '-' },
    { label: 'Call Notes', value: item.data.callNotes ?? '-' },
    { label: 'Agent', value: item.data.agentName ?? '-' }
  ];

  const getStatusLabel = () => {
    const status = item.data.patientStatus?.toLowerCase() ?? '';
    return ['active', 'pending'].includes(status) ? 'Active' : 'Inactive';
  };

  return (
    <div className={styles.activityItem}>
      <div className={styles.activityIcon}>{getIcon()}</div>
      <div className={styles.activityContent}>
        <div className={styles.activityItemTitleFlex}>
          <div className={styles.activityItemTitle}>{getTitle()} </div>
          {item.eventType === ActivityFeedType.TRANSMISSION && (
            <ActionIcon size="xs" color="plum" variant="transparent" onClick={handleOpenTxReport}>
              <IconExternalLink size={14} />
            </ActionIcon>
          )}
        </div>
        {item.eventType === ActivityFeedType.CLINICAL_NOTE && (
          <div className={styles.activityNotes}>{item.data.truncatedNoteText}</div>
        )}
        {item.eventType === ActivityFeedType.REGAL_CALL && (
          <Box>
            {regalCallFields.map((field) => (
              <div key={field.value} className={styles.activityNotes}>
                <span className={styles.fontBold}>{field.label}:</span> {field.value}
              </div>
            ))}
          </Box>
        )}
        {item.eventType === ActivityFeedType.PATIENT_STATUS && item.data.patientStatus && (
          <Box>
            <div className={styles.activityNotes}>
              <span className={styles.fontBold}>{'Status'}:</span> {getStatusLabel()}
            </div>
          </Box>
        )}
        {item.eventType === ActivityFeedType.RETELL_CALLS && (
          <Box>
            <div className={styles.activityNotes}>
              <span className={styles.fontBold}>{'Disposition'}:</span>{' '}
              {`Disconnected (${formatReason(item?.data?.disconnect_reason)})`}
            </div>
            <div className={styles.activityNotes}>
              <span className={styles.fontBold}>{'Notes'}:</span> {formatReasonNoted(item?.data?.disconnect_reason)}
            </div>
            <div className={styles.activityNotes}>
              <span className={styles.fontBold}>{'Agent ID'}:</span>{' '}
              Agentic AI
            </div>
          </Box>
        )}
        <div className={styles.activityTimestamp}>
          {item.eventType === ActivityFeedType.TRANSMISSION ? 'TXDT:' : ''} &nbsp;{date}, {time}
          &nbsp;{' '}
        </div>
        <div className={styles.activityTimestamp}>
          {item.data.dateOfService
            ? 'DOS: ' + formatUtcDate(item.data.dateOfService, 'MM/DD/YY')
            : ''}
        </div>
      </div>
    </div>
  );
};
